<template>
  <v-card elevation="0" class="mb-6">
    <v-card-title class="d-flex align-center justify-space-between">
      <div class="font-weight-bold">{{ $t("Lots Traceability") }}</div>
      <div v-if="!lot" class="text--secondary text-body-2">{{ $t("Select a lot to view traceability") }}</div>
    </v-card-title>

    <v-card-text v-if="lot">
      <v-card elevation="0" class="mb-4">
        <v-sheet color="#424242" class="pa-3 white--text d-flex align-center justify-space-between">
          <div class="font-weight-bold">{{ lotId }}</div>
          <v-icon class="white--text">mdi-chevron-up</v-icon>
        </v-sheet>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="2">
              <div class="text-caption text--secondary">{{ $t("Lot ID") }}</div>
              <div class="text-body-2 font-weight-medium">{{ lotId }}</div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption text--secondary">{{ $t("Date") }}</div>
              <div class="text-body-2 font-weight-medium">{{ lotDate }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption text--secondary">{{ $t("Product") }}</div>
              <div class="text-body-2 font-weight-medium">{{ productName }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="text-caption text--secondary">{{ $t("Quantity") }}</div>
              <div class="text-body-2 font-weight-medium">{{ quantityText }}</div>
            </v-col>
            <v-col cols="12" md="2">
              <div class="text-caption text--secondary">{{ $t("Quality Grade") }}</div>
              <div class="text-body-2 font-weight-medium">{{ qualityGrade }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-expansion-panels v-model="openPanels" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header class="panel-header farms">
            <div class="font-weight-bold">{{ $t("Farms") }}</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table>
              <thead>
                <tr>
                  <th class="text-left">{{ $t("Farm") }}</th>
                  <th class="text-left">{{ $t("Farmer Name") }}</th>
                  <th class="text-left">{{ $t("Harvest / Purchase Date") }}</th>
                  <th class="text-left">{{ $t("Lot ID") }}</th>
                  <th class="text-left">{{ $t("Quantity") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in farmsRows" :key="`farm-${idx}`">
                  <td>{{ row.farmName }}</td>
                  <td>{{ row.farmerName }}</td>
                  <td>{{ row.date }}</td>
                  <td>{{ row.orderCode }}</td>
                  <td>{{ row.quantity }}</td>
                </tr>
                <tr v-if="farmsRows.length === 0">
                  <td colspan="5" class="text-center text--secondary">{{ $t("No farms linked") }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="panel-header buying">
            <div class="font-weight-bold">{{ $t("Buying Station") }}</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table>
              <thead>
                <tr>
                  <th class="text-left">{{ $t("Purchase Order") }}</th>
                  <th class="text-left">{{ $t("Farmer / Seller") }}</th>
                  <th class="text-left">{{ $t("Reception Date") }}</th>
                  <th class="text-left">{{ $t("Quantity") }}</th>
                  <th class="text-left">{{ $t("Buying Station") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in purchaseRows" :key="`po-${idx}`">
                  <td>{{ row.orderCode }}</td>
                  <td>{{ row.farmerName }}</td>
                  <td>{{ row.date }}</td>
                  <td>{{ row.quantity }}</td>
                  <td>{{ row.station }}</td>
                </tr>
                <tr v-if="purchaseRows.length === 0">
                  <td colspan="5" class="text-center text--secondary">{{ $t("No purchase orders linked") }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <div class="mt-4">
              <div class="panel-subtitle">{{ $t("Processing Batch") }}</div>
              <v-simple-table>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t("User") }}</th>
                    <th class="text-left">{{ $t("Processing Date") }}</th>
                    <th class="text-left">{{ $t("Lot ID") }}</th>
                    <th class="text-left">{{ $t("Quantity") }}</th>
                    <th class="text-left">{{ $t("Processor") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in processingRows" :key="`pb-${idx}`">
                    <td>{{ row.user }}</td>
                    <td>{{ row.date }}</td>
                    <td>{{ row.lotId }}</td>
                    <td>{{ row.quantity }}</td>
                    <td>{{ row.processor }}</td>
                  </tr>
                  <tr v-if="processingRows.length === 0">
                    <td colspan="5" class="text-center text--secondary">{{ $t("No processing batches linked") }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="panel-header processing">
            <div class="font-weight-bold">{{ $t("Processing Station") }}</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table>
              <thead>
                <tr>
                  <th class="text-left">{{ $t("Step") }}</th>
                  <th class="text-left">{{ $t("User") }}</th>
                  <th class="text-left">{{ $t("Processing Date") }}</th>
                  <th class="text-left">{{ $t("Lot ID") }}</th>
                  <th class="text-left">{{ $t("Quantity") }}</th>
                  <th class="text-left">{{ $t("Processing Station") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in processingStationRows" :key="`ps-${idx}`">
                  <td>{{ row.step }}</td>
                  <td>{{ row.user }}</td>
                  <td>{{ row.date }}</td>
                  <td>{{ row.lotId }}</td>
                  <td>{{ row.quantity }}</td>
                  <td>{{ row.station }}</td>
                </tr>
                <tr v-if="processingStationRows.length === 0">
                  <td colspan="6" class="text-center text--secondary">{{ $t("No processing station data linked") }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "LotsTraceability",
  props: {
    lot: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      // Open all sections by default (Farms, Buying Station, Processing Station)
      openPanels: [0, 1, 2],
    };
  },
  computed: {
    type() {
      return this.lot?.type || null;
    },
    lotId() {
      if (!this.lot) return "N/A";
      return this.type === "cacao" ? this.lot.fermentationCode || "N/A" : this.lot.batchCode || "N/A";
    },
    lotDate() {
      return this.lot?.endDate || this.lot?.purchasedAt || "N/A";
    },
    productName() {
      if (!this.lot) return "N/A";
      return this.type === "cacao" ? "Cacao" : "Coffee";
    },
    quantityText() {
      if (!this.lot) return "N/A";
      if (this.type === "cacao") {
        return this.lot.finalWeight != null ? `${this.lot.finalWeight} KG` : "N/A";
      }
      return this.lot.totalCoffeeCherryQty != null ? `${this.lot.totalCoffeeCherryQty} KG` : "N/A";
    },
    qualityGrade() {
      if (!this.lot) return "N/A";
      if (this.type === "cacao") return "N/A";
      return this.lot.processingBatch?.[0]?.batchRating ?? "N/A";
    },
    farmsRows() {
      if (!this.lot) return [];
      if (this.type === "cacao") {
        const orders = this.lot.cacaoPurchaseOrder || [];
        return orders.flatMap((o) => {
          const farms = o?.cacaoPlantations?.userFarms || (o?.userFarms ? [o.userFarms] : []);
          const farmerName = o?.farmer?.fullName || "N/A";
          return (farms || []).map((f) => ({
            farmName: f?.farmName || "N/A",
            farmerName,
            date: o?.purchasedAt || "N/A",
            orderCode: o?.orderCode || "N/A",
            quantity: o?.availableWeight != null ? `${o.availableWeight} kg` : "N/A",
          }));
        });
      }

      const orders = this.lot.buyingStationOrder || [];
      return orders.flatMap((o) => {
        const farms = o?.plantations?.userFarms || (o?.userFarms ? [o.userFarms] : []);
        const farmerName = o?.farmer?.fullName || "N/A";
        return (farms || []).map((f) => ({
          farmName: f?.farmName || "N/A",
          farmerName,
          date: o?.purchasedAt || "N/A",
          orderCode: o?.orderCode || "N/A",
          quantity: o?.coffeeCherryQty != null ? `${o.coffeeCherryQty} kg` : "N/A",
        }));
      });
    },
    purchaseRows() {
      if (!this.lot) return [];
      const orders = this.type === "cacao" ? this.lot.cacaoPurchaseOrder || [] : this.lot.buyingStationOrder || [];
      return orders.map((o) => ({
        orderCode: o?.orderCode || "N/A",
        farmerName: o?.farmer?.fullName || "N/A",
        date: o?.purchasedAt || "N/A",
        quantity:
          this.type === "cacao"
            ? o?.availableWeight != null
              ? `${o.availableWeight} kg`
              : "N/A"
            : o?.coffeeCherryQty != null
              ? `${o.coffeeCherryQty} kg`
              : "N/A",
        station: o?.buyingStation?.fullName || "N/A",
      }));
    },
    processingRows() {
      if (!this.lot) return [];
      const batches = this.lot.processingBatch || [];
      return batches.map((b) => ({
        user: b?.user?.fullName || "N/A",
        date: b?.processingDate || this.lot?.endDate || "N/A",
        lotId: this.lotId,
        quantity: this.quantityText,
        processor: b?.dryMilling?.fullName || "N/A",
      }));
    },
    processingStationRows() {
      if (!this.lot) return [];
      // For coffee, we can also show dry milling / processing steps from parchment batches when present.
      const parchments = this.lot.parchmentCoffeeProcessingBatches || [];
      if (!parchments.length) return [];
      return parchments.map((p) => ({
        step: "Dry Milling",
        user: p?.parchmentCoffee?.dryMilling?.fullName || "N/A",
        date: p?.parchmentCoffee?.purchaseDate || "N/A",
        lotId: p?.huskCode || this.lotId,
        quantity: p?.quantity != null ? `${p.quantity} kg` : "N/A",
        station: p?.parchmentCoffee?.dryMilling?.fullName || "N/A",
      }));
    },
  },
};
</script>

<style scoped>
.panel-header {
  border-radius: 4px;
}
.panel-header.farms {
  background: #b7e8ce;
}
.panel-header.buying {
  background: #cfe6ff;
}
.panel-header.processing {
  background: #ffd7a1;
}
.panel-subtitle {
  font-weight: 700;
  margin-bottom: 8px;
}
</style>


